import type {
  CvHealthArea,
  CvHealthAreaStatus,
  CvHealthCheckId,
  CvHealthFinding,
  CvHealthReport,
  CvHealthStage,
} from '@tabbio/types';
import { HEALTH_HEART_SIGNAL_PATH_LENGTH, HEALTH_HEART_SIGNAL_POINTS } from './cv-health-heart-points';

/**
 * CV Health view logic shared by every surface that draws a report: the
 * app (apps/mobile native + web twins) and the public funnel on
 * cvhealth.tabbio.com (apps/web). One module, so the rings, the heart, the
 * score and the counts can never disagree between the app and the site.
 *
 * Pure: no React, no platform code, no copy. Copy keys live with each app's
 * own translation table; this file only decides numbers and shapes.
 *
 * Three parts:
 *   1. the report model (area order, ring/centre arithmetic, counts, bands,
 *      copy-key ids, stage derivation)
 *   2. the ring geometry (three concentric rounded rings, 8-unit stroke at
 *      the 168 px reference size)
 *   3. the Health heart (the artwork, the signal line, its motion samplers
 *      and the editor launcher's score-band colours and wave)
 *
 * The `'worklet'` directives are for the app's Reanimated UI thread; they are
 * plain string statements everywhere else.
 */

// ─── 1. Report model ────────────────────────────────────────────────────

export const CV_HEALTH_AREA_ORDER: readonly CvHealthArea[] = [
  'human',
  'system',
  'ai_bot',
] as const;

/** Rings are drawn outer to inner in this order: Recruiters, Hiring systems, AI tools. */
export const CV_HEALTH_RING_ORDER = CV_HEALTH_AREA_ORDER;

/**
 * Check families the current engine cannot inspect. Listed under
 * "About these checks" as "Not checked in this version". Never rendered
 * as passes.
 */
export const CV_HEALTH_NOT_CHECKED = [
  'layout',
  'fonts',
  'contrast',
  'ocr',
  'spelling',
  'job_match',
] as const;
export type CvHealthNotCheckedFamily = (typeof CV_HEALTH_NOT_CHECKED)[number];

export type CvHealthRingMode = 'checking' | 'result' | 'unavailable';

export interface CvHealthRingValue {
  area: CvHealthArea;
  /** 0..1 fraction to draw. 0 for an unavailable area (track only). */
  fraction: number;
  value: number;
  eligible: number;
  status: CvHealthAreaStatus;
}

export type CvHealthCenterValue =
  | { kind: 'percent'; value: number }
  | { kind: 'score'; value: number }
  | { kind: 'none' };

export function isFindingOpen(finding: CvHealthFinding): boolean {
  return finding.decision === null;
}

/** Open findings that count as a suggestion (optional recommendations never count). */
export function isFindingActionable(finding: CvHealthFinding): boolean {
  return isFindingOpen(finding) && finding.severity !== 'optional';
}

export function getAreaFindings(
  report: CvHealthReport,
  area: CvHealthArea,
): CvHealthFinding[] {
  return report.findings.filter(
    (finding) => finding.area === area && isFindingOpen(finding),
  );
}

export function countAreaSuggestions(
  report: CvHealthReport,
  area: CvHealthArea,
): number {
  return report.findings.filter(
    (finding) => finding.area === area && isFindingActionable(finding),
  ).length;
}

export function countSuggestions(report: CvHealthReport): number {
  return report.findings.filter(isFindingActionable).length;
}

export function hasCheckDetail(
  report: CvHealthReport,
  area?: CvHealthArea,
): boolean {
  return report.findings.some(
    (finding) =>
      isFindingOpen(finding) &&
      finding.kind === 'check_detail' &&
      (area ? finding.area === area : true),
  );
}

const SEVERITY_RANK: Record<CvHealthFinding['severity'], number> = {
  high: 0,
  medium: 1,
  low: 2,
  optional: 3,
};

/** The next outstanding finding, highest severity first, in report order. */
export function getNextFinding(
  report: CvHealthReport,
  area?: CvHealthArea,
): CvHealthFinding | null {
  const candidates = report.findings.filter(
    (finding) =>
      isFindingActionable(finding) && (area ? finding.area === area : true),
  );
  candidates.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
  return candidates[0] ?? null;
}

export function getRingMode(report: CvHealthReport | null): CvHealthRingMode {
  if (!report) return 'checking';
  if (report.status === 'running') return 'checking';
  if (report.status === 'unreadable' || report.status === 'failed') {
    return 'unavailable';
  }
  return 'result';
}

/**
 * Ring fractions. While checking: completed / eligible per area. After
 * checking: passed / eligible. An area with no eligible checks or an
 * unavailable status draws its track only.
 */
export function getRingValues(
  report: CvHealthReport | null,
  mode: CvHealthRingMode = getRingMode(report),
): CvHealthRingValue[] {
  return CV_HEALTH_RING_ORDER.map((area) => {
    const summary = report?.areas[area];
    if (!summary || mode === 'unavailable' || summary.eligible === 0) {
      return {
        area,
        fraction: 0,
        value: 0,
        eligible: summary?.eligible ?? 0,
        status: summary?.status ?? (mode === 'checking' ? 'checking' : 'unavailable'),
      };
    }
    const value = mode === 'checking' ? summary.completed : summary.passed;
    return {
      area,
      fraction: Math.max(0, Math.min(1, value / summary.eligible)),
      value,
      eligible: summary.eligible,
      status: summary.status,
    };
  });
}

/**
 * Centre of the rings. Only active checking shows a percent sign. A finished
 * result shows the bare score. Unreadable/failed shows nothing.
 */
export function getCenterValue(
  report: CvHealthReport | null,
  mode: CvHealthRingMode = getRingMode(report),
): CvHealthCenterValue {
  if (mode === 'unavailable') return { kind: 'none' };
  if (!report) return { kind: 'percent', value: 0 };
  if (mode === 'checking') {
    const percent =
      report.eligibleCount > 0
        ? Math.round((100 * report.completedCount) / report.eligibleCount)
        : 0;
    return { kind: 'percent', value: Math.max(0, Math.min(100, percent)) };
  }
  if (report.score === null) return { kind: 'none' };
  return { kind: 'score', value: report.score };
}

/** Score derived the same way the server does, for optimistic recheck display and tests. */
export function computeScore(passed: number, eligible: number): number | null {
  if (eligible <= 0) return null;
  return Math.round((100 * passed) / eligible);
}

export type CvHealthCountBand = '0' | '1' | '2' | '3' | '4_5' | '6_plus';

/** Bounded count for analytics properties. Never the raw finding list. */
export function toCountBand(count: number): CvHealthCountBand {
  if (count <= 0) return '0';
  if (count === 1) return '1';
  if (count === 2) return '2';
  if (count === 3) return '3';
  if (count <= 5) return '4_5';
  return '6_plus';
}

export type CvHealthScoreBand =
  | 'lt_40'
  | '40_59'
  | '60_79'
  | '80_99'
  | '100'
  | 'none';

export function toScoreBand(score: number | null): CvHealthScoreBand {
  if (score === null) return 'none';
  if (score >= 100) return '100';
  if (score >= 80) return '80_99';
  if (score >= 60) return '60_79';
  if (score >= 40) return '40_59';
  return 'lt_40';
}

/** Copy lookup key for a check, used with `cvHealth.checks.<key>.*`. */
export function checkCopyKey(checkId: CvHealthCheckId): string {
  return checkId.replace('.', '_');
}

/** Copy lookup key for a finding: its first check id. */
export function findingCopyKey(finding: CvHealthFinding): string {
  return checkCopyKey(finding.checkIds[0] ?? ('human.summary_specific' as CvHealthCheckId));
}

/**
 * The stage text under the heart. `phase` is the client request phase;
 * the server stage wins once a report exists.
 */
export function deriveStage(
  phase: 'idle' | 'starting' | 'running' | 'settled' | 'failed',
  report: CvHealthReport | null,
): CvHealthStage {
  if (phase === 'failed') return 'failed';
  if (phase === 'idle' || phase === 'starting' || !report) return 'reading';
  if (report.status === 'failed') return 'failed';
  if (report.status !== 'running') return 'ready';
  return report.stage;
}

/** True when the report can still lead somewhere useful (Continue, editor rows). */
export function isReportUsable(report: CvHealthReport | null): boolean {
  return !!report && (report.status === 'complete' || report.status === 'partial');
}

// ─── 2. Ring geometry ───────────────────────────────────────────────────

export const HEALTH_RING_COUNT = 3;

/** The reference size the stroke is specified at. */
export const HEALTH_RING_REFERENCE_SIZE = 168;

/** Stroke width at the reference size. */
export const HEALTH_RING_STROKE = 8;

/** Thinnest stroke at any size, so small rings stay legible. */
export const HEALTH_RING_MIN_STROKE = 6;

/** Gap between two rings, as a fraction of the stroke. */
export const HEALTH_RING_GAP_RATIO = 0.75;

/** Centre text size as a fraction of the ring size. */
export const HEALTH_RING_CENTER_FONT_RATIO = 0.2;

/** A changed ring settles over this long. Reduced motion settles immediately. */
export const HEALTH_RING_ANIMATION_MS = 600;

/**
 * The ring easing: `motion.easing.move` from `@tabbio/tokens`
 * (`cubic-bezier(0.65, 0, 0.35, 1)`), as numbers for Reanimated's
 * `Easing.bezier`. CSS uses the same curve.
 */
export const HEALTH_RING_EASING = [0.65, 0, 0.35, 1] as const;

/** Theme keys (app native) for each ring's arc, outer to inner. */
export type HealthRingColorKey = 'healthPeople' | 'healthSystems' | 'healthAi';

export const HEALTH_RING_COLOR_KEYS: Record<CvHealthArea, HealthRingColorKey> = {
  human: 'healthPeople',
  system: 'healthSystems',
  ai_bot: 'healthAi',
};

/**
 * CSS variables for the same colours in the app's web build (apps/mobile
 * global.css). The public site names them `--color-health-*` (Tailwind
 * `@theme`) and passes its own map to `describeRings`.
 */
export const HEALTH_RING_CSS_VARS: Record<CvHealthArea, string> = {
  human: 'var(--health-people)',
  system: 'var(--health-systems)',
  ai_bot: 'var(--health-ai)',
};

export interface HealthRingGeometry {
  area: CvHealthArea;
  /** 0 = outer (human), 1 = middle (system), 2 = inner (ai_bot). */
  index: number;
  radius: number;
  circumference: number;
  colorKey: HealthRingColorKey;
  cssVar: string;
}

export interface HealthRingsLayout {
  size: number;
  /** Centre of the rings on both axes. */
  center: number;
  stroke: number;
  gap: number;
  rings: HealthRingGeometry[];
  /** Font size of the centre score / percentage. */
  centerFontSize: number;
}

export function getRingStroke(size: number): number {
  return Math.max(
    HEALTH_RING_MIN_STROKE,
    Math.round((size * HEALTH_RING_STROKE) / HEALTH_RING_REFERENCE_SIZE),
  );
}

/**
 * Radii and circumferences for all three rings at `size`. The outer ring's
 * stroke touches the edge of the box; each inner ring sits one stroke plus
 * one gap further in. `cssVars` names the arc colours for a web renderer
 * (defaults to the app's variables).
 */
export function describeRings(
  size: number,
  cssVars: Readonly<Record<CvHealthArea, string>> = HEALTH_RING_CSS_VARS,
): HealthRingsLayout {
  const stroke = getRingStroke(size);
  const gap = stroke * HEALTH_RING_GAP_RATIO;
  const center = size / 2;
  const outerRadius = center - stroke / 2;
  const rings = CV_HEALTH_RING_ORDER.slice(0, HEALTH_RING_COUNT).map(
    (area, index): HealthRingGeometry => {
      const radius = Math.max(0, outerRadius - index * (stroke + gap));
      return {
        area,
        index,
        radius,
        circumference: 2 * Math.PI * radius,
        colorKey: HEALTH_RING_COLOR_KEYS[area],
        cssVar: cssVars[area],
      };
    },
  );
  return {
    size,
    center,
    stroke,
    gap,
    rings,
    centerFontSize: Math.round(size * HEALTH_RING_CENTER_FONT_RATIO),
  };
}

/** Plan-contract alias of `describeRings`. */
export const describeRing = describeRings;

export function clampFraction(fraction: number): number {
  if (!Number.isFinite(fraction)) return 0;
  return Math.max(0, Math.min(1, fraction));
}

/**
 * `stroke-dashoffset` that draws `fraction` of a ring whose dash array is one
 * full circumference: 0 draws the whole ring, `circumference` draws nothing.
 */
export function ringDashOffset(circumference: number, fraction: number): number {
  'worklet';
  const clamped = Number.isFinite(fraction) ? Math.max(0, Math.min(1, fraction)) : 0;
  return circumference * (1 - clamped);
}

/**
 * Rounded caps paint a dot even for a zero-length dash, so an empty arc is
 * hidden outright rather than drawn at zero length.
 */
export const HEALTH_RING_MIN_VISIBLE_FRACTION = 0.001;

export function ringArcOpacity(fraction: number): number {
  'worklet';
  return fraction > HEALTH_RING_MIN_VISIBLE_FRACTION ? 1 : 0;
}

/** `rotate(-90 c c)`: every arc starts at 12 o'clock and runs clockwise. */
export function ringStartTransform(center: number): string {
  return `rotate(-90 ${center} ${center})`;
}

// ─── 3. Health heart ────────────────────────────────────────────────────
//
// The AI Studio heart (the design's HEALTH_HEART_BODY / HEALTH_HEART_SIGNAL
// in cv-health.js): a soft filled heart on a 39.7 x 37 box drawn in a
// `-2 -3 44 44` viewBox, with an ECG-like signal line across its middle.
// Two uses share the artwork:
//
//   mark      the signal is cut out of the body (web: a luminance mask over
//             HEALTH_HEART_BOX; native: the line drawn in the page colour).
//             Working: ink colour, the body breathes 0.65 -> 1 -> 0.65 over
//             2.4 s and the signal draws itself over the first 1.4 s of each
//             cycle, then holds. Resting: still, the signal fully drawn, in
//             the text colour (the design's --p-text), never muted.
//   launcher  the editor dock's Heart Agent (design A4): a solid body in the
//             toolbar icon colour under a coloured signal line, tinted by the
//             score band and moving as a calm wave along the 80 points
//             resampled from the signal (HEALTH_HEART_SIGNAL_POINTS).

export const HEALTH_HEART_VIEWBOX = '-2 -3 44 44';
/** The viewBox as numbers: the mask rectangle and any full-box fill. */
export const HEALTH_HEART_BOX = { x: -2, y: -3, width: 44, height: 44 } as const;

/** The heart body (the design's HEALTH_HEART_BODY), fill only. */
export const HEALTH_HEART_PATH =
  'M28.9766 0C35.1692 0 39.7265 4.58091 39.7266 11.3936C39.7266 21.1427 31.3134 28.3076 23.3682 35.4727C22.3167 36.4123 21.1484 36.9999 19.8633 37C18.578 37 17.409 36.4123 16.3574 35.4727C8.41227 28.3077 0 21.1426 0 11.3936C4.25883e-05 4.58103 4.55658 0.000157438 10.749 0C14.6048 0 17.7601 1.87963 19.8633 4.93359C21.9664 1.87966 25.1208 4.75923e-05 28.9766 0Z';

/**
 * The signal line (the design's HEALTH_HEART_SIGNAL): M, H and cubic
 * segments, left to right across the body. `scripts/heart-signal-points.mjs`
 * reads this constant to regenerate `cv-health-heart-points.ts`.
 */
export const HEALTH_SIGNAL_PATH =
  'M6.6 17H12C13 17 13.4 15.3 14.2 15.3C15 15.3 15.2 18.7 16 18.7C16.8 18.7 17.2 17 18 17C18.8 17 19.1 12 20 12C20.9 12 21.1 21.0 22 21.0C22.9 21.0 23.2 17 24 17C24.8 17 25 15.6 25.8 15.6C26.6 15.6 27.2 17 28 17H32.7';

/** Stroke of the signal cut out of the mark. */
export const HEALTH_SIGNAL_STROKE_WIDTH = 2;
/** Stroke of the coloured signal line drawn over the launcher's solid body. */
export const HEALTH_HEART_LINE_STROKE_WIDTH = 2.1;

export { HEALTH_HEART_SIGNAL_POINTS, HEALTH_HEART_SIGNAL_PATH_LENGTH };

export function polylineLength(points: readonly (readonly [number, number])[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const [x0, y0] = points[i - 1]!;
    const [x1, y1] = points[i]!;
    total += Math.hypot(x1 - x0, y1 - y0);
  }
  return total;
}

/** Length of the signal line in viewBox units: its dash array while drawing. */
export const HEALTH_SIGNAL_LENGTH = HEALTH_HEART_SIGNAL_PATH_LENGTH;

// The launcher's signal colour follows the score band (design
// health-heart-morph.js:45); grey while there is no settled score (no report,
// checking, a stale or failed report).
export type HealthHeartSignalBand = HealthScoreStrength | 'none';

export const HEALTH_HEART_SIGNAL_BAND: Record<HealthHeartSignalBand, string> = {
  strong: '#67d4a0',
  moderate: '#f0bc63',
  weak: '#ee9388',
  none: '#a1a1aa',
};

/** The launcher line's colour for a settled score, or the grey for none. */
export function getHeartSignalColor(score: number | null | undefined): string {
  return HEALTH_HEART_SIGNAL_BAND[getScoreStrength(score) ?? 'none'];
}

/**
 * The launcher's wave (health-heart-morph.js:50-57): each of the 80 points
 * moves vertically by `amplitude * sin(pi u)^envelopePow * sin(cycles pi u -
 * phase)`, with `u` the point's share of the line, so the ends stay put and
 * the middle sways. `phase` grows by one radian every `msPerRad`, so a loop
 * of `periodMs` repeats seamlessly.
 */
export const HEALTH_HEART_WAVE = {
  amplitude: 2.2,
  envelopePow: 1.4,
  cycles: 3,
  msPerRad: 800,
  periodMs: 2 * Math.PI * 800,
} as const;

/**
 * The signal as an `M ... L ...` path through `points`, each displaced by the
 * wave at `phase` (radians): a snapshot of the sway, never the line at rest
 * (that is HEALTH_SIGNAL_PATH itself). Both ends always stay on the body.
 */
export function heartSignalPath(
  points: readonly (readonly [number, number])[],
  phase: number,
): string {
  'worklet';
  const last = points.length - 1;
  let d = '';
  for (let i = 0; i <= last; i += 1) {
    const [x, y] = points[i]!;
    const u = last > 0 ? i / last : 0;
    const envelope = Math.pow(Math.sin(Math.PI * u), HEALTH_HEART_WAVE.envelopePow);
    const offset =
      HEALTH_HEART_WAVE.amplitude * envelope * Math.sin(HEALTH_HEART_WAVE.cycles * Math.PI * u - phase);
    d += `${i === 0 ? 'M' : ' L'}${x.toFixed(3)} ${(y + offset).toFixed(3)}`;
  }
  return d;
}

export const HEALTH_HEART_MOTION = {
  /** One heartbeat: breathing and the pulse draw share this cycle. */
  cycleMs: 2400,
  /** Body opacity: dim at the ends of the cycle, full at the middle. */
  breathe: { min: 0.65, max: 1, peakAt: 0.5 },
  /** The pulse line draws over this long, then holds for the rest of the cycle. */
  drawMs: 1400,
} as const;

/** Where in the cycle the pulse line finishes drawing (0..1). */
export const HEALTH_SIGNAL_DRAW_END = HEALTH_HEART_MOTION.drawMs / HEALTH_HEART_MOTION.cycleMs;

/** Default heart size beside a status line. */
export const HEALTH_HEART_DEFAULT_SIZE = 18;

/** Body opacity at cycle position `t`: CSS `ease-in-out` to the peak and back. */
export function sampleHeartBreathe(t: number): number {
  'worklet';
  const clamped = t - Math.floor(t);
  const { min, max, peakAt } = HEALTH_HEART_MOTION.breathe;
  const local = clamped <= peakAt ? clamped / peakAt : 1 - (clamped - peakAt) / (1 - peakAt);
  // ease-in-out is visually a smoothstep at this size.
  const eased = local * local * (3 - 2 * local);
  return min + (max - min) * eased;
}

/**
 * `stroke-dashoffset` of the pulse line at cycle position `t`: from its full
 * length (hidden) to 0 (drawn) with an ease-out over the draw window, then 0
 * for the hold.
 */
export function sampleSignalOffset(t: number, length: number = HEALTH_SIGNAL_LENGTH): number {
  'worklet';
  const clamped = t - Math.floor(t);
  if (clamped >= HEALTH_SIGNAL_DRAW_END) return 0;
  const local = clamped / HEALTH_SIGNAL_DRAW_END;
  const eased = 1 - (1 - local) * (1 - local) * (1 - local);
  return length * (1 - eased);
}

// ─── 4. Score gauge ─────────────────────────────────────────────────────
//
// The settled score as a half-circle gauge (the 30 Sep 2026 design, adapted
// from designali-in's Financial Score Cards): a 100 x 50 box, radius 45,
// stroke 10, flat ends on the baseline, a three-stop gradient whose hue
// follows the score's strength, and the bare score under the arc. The gauge
// replaces the three rings on every report; the checking state is a slim
// progress line with four named steps instead (see section 5).

export const HEALTH_GAUGE_WIDTH = 100;
export const HEALTH_GAUGE_HEIGHT = 50;
export const HEALTH_GAUGE_RADIUS = 45;
export const HEALTH_GAUGE_STROKE = 10;
/** Left end to right end over the top, on a `pathLength` of 100. */
export const HEALTH_GAUGE_PATH = 'M 5 50 A 45 45 0 0 1 95 50';
/** The path's own length, for renderers that cannot use `pathLength`. */
export const HEALTH_GAUGE_ARC_LENGTH = Math.PI * HEALTH_GAUGE_RADIUS;
/** Widest the gauge draws, in px; it shrinks with its column below that. */
export const HEALTH_GAUGE_MAX_WIDTH = 176;

export type HealthScoreStrength = 'strong' | 'moderate' | 'weak';

/** 80 and up is strong, 40 and up moderate, below that weak. No score, no strength. */
export function getScoreStrength(score: number | null | undefined): HealthScoreStrength | null {
  if (typeof score !== 'number' || !Number.isFinite(score)) return null;
  if (score >= 80) return 'strong';
  if (score >= 40) return 'moderate';
  return 'weak';
}

export interface HealthGaugeStops {
  light: string;
  mid: string;
  deep: string;
}

/**
 * Gradient stops (left to right) per strength: hsl(142 71% / 38 92% / 0 84%)
 * at 80, 60 and 40 % lightness, as hex for renderers without `hsl()`.
 */
export const HEALTH_GAUGE_STOPS: Record<HealthScoreStrength, HealthGaugeStops> = {
  strong: { light: '#a8f0c2', mid: '#51e186', deep: '#1eae53' },
  moderate: { light: '#fbd99d', mid: '#f7b23b', deep: '#c47f08' },
  weak: { light: '#f7a1a1', mid: '#ef4343', deep: '#bc1010' },
};

/** Where the middle stop sits along the arc (0..1). */
export const HEALTH_GAUGE_MID_STOP = 0.55;

export const HEALTH_GAUGE_MOTION = {
  /** A first result: the arc waits, then sweeps from empty to the score. */
  drawDelayMs: 400,
  drawMs: 1000,
  drawEasing: [0.65, 0, 0.35, 1],
  /** Each digit slides up into place, the later ones a little slower. */
  digitDelayMs: 400,
  digitStaggerMs: 100,
  digitMs: 800,
  digitExtraMs: 300,
  digitEasing: [0.16, 1, 0.3, 1],
  /** A changed score: number and arc move together from the old value. */
  updateMs: 420,
} as const;

/** `stroke-dashoffset` on a `pathLength` of 100 that draws `score` of the arc. */
export function gaugeDashOffset(score: number): number {
  'worklet';
  const clamped = Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : 0;
  return 100 - clamped;
}

/** Ease-out cubic, the curve of a score update. */
export function easeOutCubic(t: number): number {
  'worklet';
  const clamped = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - clamped, 3);
}

// ─── 5. Checking steps ──────────────────────────────────────────────────
//
// While a review runs: a slim line filled with the share of checks done
// (never a timer) and four named steps that follow the server's stages.

export const CV_HEALTH_CHECK_STEPS = [
  'reading',
  'sections_links',
  'skills_experience',
  'preparing',
] as const satisfies readonly CvHealthStage[];

export type CvHealthCheckStep = (typeof CV_HEALTH_CHECK_STEPS)[number];
export type CvHealthCheckStepState = 'done' | 'active' | 'pending';

/** Each step's state at `stage`: earlier ones done, this one active. `ready` marks all done. */
export function getCheckStepStates(
  stage: CvHealthStage,
): { step: CvHealthCheckStep; state: CvHealthCheckStepState }[] {
  const current =
    stage === 'ready' ? CV_HEALTH_CHECK_STEPS.length : CV_HEALTH_CHECK_STEPS.indexOf(stage as CvHealthCheckStep);
  const active = current < 0 ? 0 : current;
  return CV_HEALTH_CHECK_STEPS.map((step, index) => ({
    step,
    state: index < active ? 'done' : index === active ? 'active' : 'pending',
  }));
}

/** Share of checks done (0..100) while a review runs; 100 once it settled; 0 without a report. */
export function getCheckProgress(report: CvHealthReport | null, stage?: CvHealthStage): number {
  if (stage === 'ready') return 100;
  if (!report) return 0;
  if (report.status !== 'running') return 100;
  if (report.eligibleCount <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((100 * report.completedCount) / report.eligibleCount)));
}

/**
 * The checking line is Material 3 Expressive's wavy determinate indicator:
 * one wave every 30 px swinging 3 px, as quadratic halves along a 12 px
 * strip long enough for any card. The fill clips it to the share of checks
 * done, and both platforms slide it one wavelength per loop, toward the end.
 */
export const HEALTH_CHECK_WAVE = {
  wavelength: 30,
  amplitude: 3,
  height: 12,
  stroke: 4,
  loopMs: 1800,
} as const;
export const HEALTH_CHECK_WAVE_SPAN = 1800;
export const HEALTH_CHECK_WAVE_PATH = ((): string => {
  const { wavelength, amplitude, height } = HEALTH_CHECK_WAVE;
  const half = wavelength / 2;
  // A quadratic half wave peaks at half its control point's offset.
  let path = `M0 ${height / 2} q ${half / 2} ${-2 * amplitude} ${half} 0`;
  for (let x = half; x < HEALTH_CHECK_WAVE_SPAN; x += half) path += ` t ${half} 0`;
  return path;
})();

/** The caption under the line: "Checks completed  24/26". */
export interface CvHealthCheckCount {
  completed: number;
  total: number;
}

/**
 * What the line counts, as whole checks, or null to leave the caption empty.
 * A `running` report has its deterministic checks done and the AI pass still
 * going, so the line can stand near full while a step is still active: the
 * caption says how many checks are actually left. It must never claim more
 * than the server reported.
 */
export function getCheckCount(
  report: CvHealthReport | null,
  stage?: CvHealthStage,
): CvHealthCheckCount | null {
  // TODO(human): decide when the caption is truthful, and what it says.
  return null;
}
